import React, { createContext, FC, useEffect, useReducer } from 'react';
import { getLogger } from '@packages/core/logging';
import { CidCvnActionDispatcher, cidCvnActions, cidCvnReducer, CidCvnState, initialState } from './cid-cvn.state';
import { CidCvnEligibilityResponse, CidCvnCheckResult } from '@packages/models/api/cid-cvn';
import { CidCvnService } from './cid-cvn.service';

const logger = getLogger('cid-cvn');

export const CidCvnStateContext = createContext<CidCvnState | undefined>(undefined);
export const CidCvnDispatchContext = createContext<CidCvnActionDispatcher | undefined>(undefined);

interface CidCvnProviderProps {
    children: React.ReactNode;
    cidCvnService: CidCvnService;
    vin?: string;
}

export const CidCvnProvider: FC<CidCvnProviderProps> = ({ children, cidCvnService, vin }) => {
    const [state, dispatch] = useReducer(cidCvnReducer, initialState);

    useEffect(() => {
        if (!vin) {
            return;
        }
        const abortController = new AbortController();

        fetchCidCvnData(vin, true, cidCvnService, dispatch);

        return () => {
            abortController.abort();
            dispatch(cidCvnActions.clearCidCvnData());
        };
    }, [vin, cidCvnService]);

    return (
        <CidCvnStateContext.Provider value={state}>
            <CidCvnDispatchContext.Provider value={dispatch}>{children}</CidCvnDispatchContext.Provider>
        </CidCvnStateContext.Provider>
    );
};

async function fetchCidCvnEligibility(vin: string, cidCvnService: CidCvnService, dispatch: CidCvnActionDispatcher) {
    try {
        const response = await cidCvnService.getCidCvnEligibility({ vin });
        if (response.success && response.data) {
            const { cidCvnEligible, vinLockedout } = response.data as CidCvnEligibilityResponse;
            dispatch(cidCvnActions.setCidCvnEligibility({ cidCvnEligible }));
            dispatch(cidCvnActions.setCidCvnCheckBlock({ vinLockedout }));
            return cidCvnEligible;
        }
        dispatch(cidCvnActions.setErrors({ errors: ['Unable to retrieve CID/CVN Eligibility.'] }));
        return false;
    } catch (err) {
        const error = err as Error;
        logger.error('Failed to fetch CID/CVN eligibility', { error, vin });
        dispatch(cidCvnActions.setErrors({ errors: [error.message] }));
        return false;
    }
}

async function fetchCidCvnList(vin: string, cidCvnService: CidCvnService, dispatch: CidCvnActionDispatcher) {
    try {
        const response = await cidCvnService.getCidCvnList(vin);
        if (response.success && response.data) {
            dispatch(cidCvnActions.setCidCvnList({ cidCvnList: response.data }));
        } else {
            dispatch(cidCvnActions.setErrors({ errors: ['Unable to retrieve CID/CVN list.'] }));
        }
    } catch (err) {
        const error = err as Error;
        logger.error('Failed to fetch the CID/CVN list', { error, vin });
        dispatch(cidCvnActions.setErrors({ errors: [error.message] }));
    }
}

export function clearCidCvnErrors(dispatch: CidCvnActionDispatcher) {
    dispatch(cidCvnActions.setErrors({ errors: [] }));
}

export function setSelectedReportHeader(
    dispatch: CidCvnActionDispatcher,
    selectedReportHeader: { id: string | null; reportName: string | null; result: CidCvnCheckResult | null }
) {
    dispatch(cidCvnActions.setSelectedReportHeader({ selectedReportHeader }));
}

export function clearSelectedReportHeader(dispatch: CidCvnActionDispatcher) {
    dispatch(
        cidCvnActions.setSelectedReportHeader({
            selectedReportHeader: { id: null, reportName: null, result: null },
        })
    );
}

export async function fetchCidCvnData(
    vin: string,
    toggleLoading: boolean = true,
    cidCvnService: CidCvnService,
    dispatch: CidCvnActionDispatcher,
    cidCvnEligible: boolean = false
) {
    dispatch(cidCvnActions.setErrors({ errors: [] }));

    if (toggleLoading) {
        dispatch(cidCvnActions.setLoading({ isLoading: true }));
    }

    try {
        if (cidCvnEligible) {
            await fetchCidCvnList(vin, cidCvnService, dispatch);
            return;
        }

        const isVinCidCvnEligible = await fetchCidCvnEligibility(vin, cidCvnService, dispatch);
        if (isVinCidCvnEligible) {
            await fetchCidCvnList(vin, cidCvnService, dispatch);
        } else {
            dispatch(cidCvnActions.clearCidCvnData());
        }
    } catch (err) {
        const error = err as Error;
        logger.error('Failed to load the CID/CVN data', { error, vin });
        dispatch(cidCvnActions.setErrors({ errors: [error.message] }));
    } finally {
        if (toggleLoading) {
            dispatch(cidCvnActions.setLoading({ isLoading: false }));
        }
    }
}

export async function setCidCvnCheckBlock(vin: string, cidCvnService: CidCvnService, dispatch: CidCvnActionDispatcher) {
    try {
        const response = await cidCvnService.setCidCvnCheckLockout(vin);
        if (!response.success) {
            throw response.data;
        }
        dispatch(cidCvnActions.setCidCvnCheckBlock({ vinLockedout: true }));
    } catch (err) {
        const error = err as Error;
        logger.error('Failed to lock out the VIN from the CID/CVN check', { error, vin });
    }
}
